import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Upload, Eye, CheckCircle2, AlertTriangle, Loader2, RotateCcw } from "lucide-react";
import { Badge } from "./ui/badge";

/**
 * Iter36 — 2-step CSV/Excel import dialog:
 *   1. PREVIEW (dry-run): user picks file → "Provjeri" → backend computes diff
 *      without touching DB → table shows what will change.
 *   2. APPLY: user confirms → real upload → final summary.
 *
 * The dialog now exposes 4 callbacks instead of 1:
 *   - onFileChange(file)
 *   - onPreview() — runs dry_run=true
 *   - onApply() — runs the real upload (only enabled after preview)
 *   - onReset() — back to idle state
 *
 * `result` shape: { dry_run, added, updated, unchanged, skipped, errors, changes }
 */
export function EmployeeImportDialog({
  open, onClose, file, onFileChange,
  result, busy, mode,   // mode: "idle" | "preview" | "applied"
  onPreview, onApply, onReset,
}) {
  const isPreview = mode === "preview" && result?.dry_run;
  const isApplied = mode === "applied" && result && !result.dry_run;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-primary"/>
            {isApplied ? "Uvoz dovršen" : isPreview ? "Pregled promjena prije uvoza" : "Uvoz zaposlenika"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* IDLE: file picker + instructions */}
          {mode === "idle" && (
            <>
              <p className="text-muted-foreground">
                Podržani formati: <b>CSV</b> (UTF-8, separator zarez) i <b>Excel</b> (.xlsx).
                Prvi red mora sadržavati nazive stupaca. Email je <b>ključ</b> — postojeći zaposlenici se ažuriraju,
                novi se dodaju.
              </p>
              <div className="bg-secondary/50 rounded p-3 text-xs space-y-1">
                <p className="font-medium">Tipični workflow:</p>
                <ol className="list-decimal list-inside ml-2 space-y-0.5">
                  <li>Klikni &quot;Izvoz Excel&quot; da preuzmeš trenutno stanje.</li>
                  <li>Otvori .xlsx u Excelu/Google Sheets/LibreOffice, izvrši promjene.</li>
                  <li>Vrati se ovdje, odaberi datoteku i klikni &quot;Provjeri&quot; za pregled promjena.</li>
                  <li>Nakon što provjeriš listu, klikni &quot;Potvrdi i uvezi&quot;.</li>
                </ol>
                <p className="mt-2 text-amber-700">⚠️ Prazne ćelije <b>ne brišu</b> postojeće vrijednosti — samo non-empty stupci se primjenjuju kao ažuriranje.</p>
              </div>

              <div className="space-y-1">
                <Label>Datoteka (CSV ili XLSX)</Label>
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                  disabled={busy}
                  data-testid="import-file-input"
                />
                {file && (
                  <p className="text-xs text-muted-foreground">Odabrano: <b>{file.name}</b> ({Math.round(file.size / 1024)} KB)</p>
                )}
              </div>
            </>
          )}

          {/* PREVIEW: summary stats + diff table */}
          {(isPreview || isApplied) && (
            <>
              {/* Top summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid={isApplied ? "import-result-applied" : "import-result-preview"}>
                <SummaryStat label="Dodano novih" value={result.added} colorClass="text-emerald-700" bgClass="bg-emerald-50 border-emerald-200" />
                <SummaryStat label="Ažurirano" value={result.updated} colorClass="text-blue-700" bgClass="bg-blue-50 border-blue-200" />
                <SummaryStat label="Bez promjena" value={result.unchanged} colorClass="text-slate-600" bgClass="bg-slate-50 border-slate-200" />
                <SummaryStat label="Preskočeno" value={result.skipped} colorClass="text-amber-700" bgClass="bg-amber-50 border-amber-200" />
                <SummaryStat label="Greške" value={result.errors?.length || 0} colorClass={result.errors?.length ? "text-red-700" : "text-slate-500"} bgClass={result.errors?.length ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"} />
              </div>

              {/* Errors */}
              {result.errors?.length > 0 && (
                <details className="border border-red-200 bg-red-50 rounded p-3 text-xs" open data-testid="import-errors-block">
                  <summary className="cursor-pointer text-red-800 font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4"/>
                    {result.errors.length} greška(grešaka) — neće biti uvezeno
                  </summary>
                  <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto" data-testid="import-errors-list">
                    {result.errors.map((er, i) => (
                      <li key={`${er.row}-${er.email || i}`} className="text-red-800">
                        <b>Red {er.row}</b>{er.email ? <> ({er.email})</> : null}: {er.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {/* Changes table */}
              {result.changes?.length > 0 ? (
                <div className="border rounded text-xs" data-testid="import-changes-table">
                  <p className="px-3 py-2 bg-secondary/50 font-medium flex items-center gap-2 border-b">
                    <Eye className="w-4 h-4"/>
                    {isPreview
                      ? <>Pregled promjena: <b>{result.changes.length}</b> {result.changes.length === 1 ? "zaposlenik" : "zaposlenika"}</>
                      : <>Primijenjene promjene: <b>{result.changes.length}</b></>}
                    {result.changes.length === 200 && <Badge variant="secondary" className="ml-2 text-[10px]">prvih 200</Badge>}
                  </p>
                  <div className="max-h-[40vh] overflow-y-auto divide-y">
                    {result.changes.map((c, idx) => (
                      <div key={`${c.email}-${idx}`} className="px-3 py-2 hover:bg-secondary/30">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={c.action === "insert" ? "default" : "secondary"} className="text-[10px]">
                            {c.action === "insert" ? "DODAJ" : "AŽURIRAJ"}
                          </Badge>
                          <span className="font-medium">{c.full_name}</span>
                          <span className="text-muted-foreground">· {c.email}</span>
                          <span className="text-muted-foreground ml-auto text-[10px]">Red {c.row}</span>
                        </div>
                        <ul className="ml-4 space-y-0.5 text-muted-foreground">
                          {c.diff.map((d) => (
                            <li key={d.field}>
                              <span className="font-medium text-foreground">{d.label}:</span>{" "}
                              {c.action === "insert" ? (
                                <span className="text-emerald-700">{String(d.new) || "—"}</span>
                              ) : (
                                <>
                                  <span className="line-through opacity-60">{String(d.old) || "—"}</span>
                                  {" → "}
                                  <span className="text-blue-700 font-medium">{String(d.new) || "—"}</span>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted-foreground italic py-4 border rounded">
                  Nema promjena za primjenu — sve vrijednosti u datoteci su iste kao u bazi.
                </div>
              )}

              {/* Status banner */}
              {isPreview && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                  <span>Ovo je <b>pregled</b> — promjene još nisu spremljene. Klikni &quot;Potvrdi i uvezi&quot; za pravu primjenu, ili &quot;Promijeni datoteku&quot; za novi pokušaj.</span>
                </div>
              )}
              {isApplied && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-800 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                  <span>Sve promjene su <b>uspješno primijenjene</b> na bazu.</span>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          {mode === "idle" && (
            <>
              <Button variant="outline" onClick={onClose} data-testid="import-cancel-btn">Zatvori</Button>
              <Button onClick={onPreview} disabled={!file || busy} data-testid="import-preview-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <Eye className="w-4 h-4 mr-1.5"/>}
                Provjeri
              </Button>
            </>
          )}
          {isPreview && (
            <>
              <Button variant="outline" onClick={onReset} disabled={busy} data-testid="import-reset-btn">
                <RotateCcw className="w-4 h-4 mr-1.5"/>Promijeni datoteku
              </Button>
              <Button onClick={onApply} disabled={busy || (result?.changes?.length || 0) === 0} data-testid="import-apply-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <CheckCircle2 className="w-4 h-4 mr-1.5"/>}
                Potvrdi i uvezi ({result?.changes?.length || 0})
              </Button>
            </>
          )}
          {isApplied && (
            <Button onClick={onClose} data-testid="import-close-applied-btn">Zatvori</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SummaryStat({ label, value, colorClass, bgClass }) {
  return (
    <div className={`border rounded px-3 py-2 ${bgClass}`}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold ${colorClass}`}>{value || 0}</p>
    </div>
  );
}
